import NavigationError from "@/errors/navigation-error";
import { api } from "@/lib/openapi/api-client";
import FetchApiError from "@/lib/openapi/api-error";
import { ExtractResponse } from "@/lib/openapi/hooks";
import isWebApp from "@/utils/isWebApp";
import normalizeObject from "@/utils/normalize-object";
import {
    DefaultError,
    useSuspenseQuery,
    UseSuspenseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";

export type UseInitialUserResponse = ExtractResponse<
  "telegramAuthorization",
  "/initial",
  "get"
>;

const initDataSchema = z.object({
  auth_date: z.coerce.number(),
  hash: z.string(),
  signature: z.string(),
  can_send_after: z.coerce.number().optional(),
  chat: z.string().optional(),
  chat_type: z
    .enum(["sender", "private", "group", "supergroup", "channel"])
    .optional(),
  chat_instance: z.string().optional(),
  query_id: z.string().optional(),
  receiver: z.string().optional(),
  start_param: z.string().optional(),
  user: z.string().optional(),
});

const useInitialUserQuery = <TData = UseInitialUserResponse>(
  options: Omit<
    UseSuspenseQueryOptions<UseInitialUserResponse, DefaultError, TData, ["telegramAuthorization", "initial"]>,
    "queryKey" | "queryFn"
  > = {}
) => {
  const query = useSuspenseQuery({
    queryKey: ["telegramAuthorization", "initial"],
    retry: false,
    queryFn: async () => {
      try {
        return await api.telegramAuthorization("/initial", "get", {
          authorization: true,
        });
      } catch (error) {
        if (error instanceof FetchApiError) {
          const { status } = error.response;
          if (status === 409 || status === 500) {
            try {
              if (isWebApp()) {
                const { WebApp } = window.Telegram;
                const params = new URLSearchParams(WebApp.initData);
                const obj = Object.fromEntries(params.entries());
                const validated = initDataSchema.parse(obj);
                await api.telegramAuthorization("/login/web-app", "post", {
                  body: normalizeObject(validated),
                });
              }
              return await api.telegramAuthorization("/refresh", "get");
            } catch {
              throw new NavigationError("/login");
            }
          }
        }
        throw new NavigationError("/login");
      }
    },
    ...options,
  });

  return query;
};

export default useInitialUserQuery;
